import { useQuery, useMutation } from "@tanstack/react-query";
import { Link, useParams } from "react-router-dom";
import { cases } from "@/lib/endpoints";
import { AppHeader } from "@/components/AppHeader";
import { cn } from "@/lib/utils";

export function CaseDetailPage() {
  const { id } = useParams<{ id: string }>();

  const caseQ = useQuery({
    queryKey: ["case", id],
    queryFn: () => cases.get(id!),
    enabled: !!id,
    staleTime: 0,
  });

  const evalQ = useQuery({
    queryKey: ["case", id, "evaluation"],
    queryFn: () => cases.evaluation(id!),
    enabled: !!id,
  });

  const jobsQ = useQuery({
    queryKey: ["case", id, "jobs"],
    queryFn: () => cases.listJobs(id!),
    enabled: !!id,
    staleTime: 30_000,
  });

  const latestDoneJob = jobsQ.data?.items.find((j) => j.state === "done");

  if (caseQ.isLoading) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-slate-200 border-t-blue-600 rounded-full animate-spin" />
      </div>
    );
  }

  if (caseQ.isError || !caseQ.data) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center text-slate-500 text-sm">
        Could not load this case.
      </div>
    );
  }

  const c = caseQ.data;
  const eval_ = evalQ.data;
  const isPaid = !!c.paid_at;
  const isEligible = c.eligibility_status === "eligible";
  const isReady = eval_?.ready ?? false;
  const missingCount = eval_?.missing_data.length ?? 0;
  const feeDollars = c.fee_total_cents ? (c.fee_total_cents / 100).toFixed(0) : "710";

  // Determine primary CTA
  const ctaHref = isReady && isPaid
    ? `/cases/${id}/wizard/review`
    : isPaid
      ? `/cases/${id}/wizard`
      : isEligible
        ? `/cases/${id}/wizard/checkout`
        : `/cases/${id}/wizard`;

  const ctaLabel = isReady && isPaid
    ? "Generate my N-400 →"
    : isPaid
      ? "Continue application →"
      : isEligible
        ? "Pay & unlock packet →"
        : "Continue application →";

  return (
    <div className="min-h-screen bg-slate-100 flex flex-col">
      <AppHeader />

      <main className="flex-1 max-w-3xl mx-auto w-full px-6 py-10">
        {/* Back */}
        <Link
          to="/dashboard"
          className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-400 hover:text-blue-600 transition-colors mb-6"
        >
          <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5">
            <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
          </svg>
          Back to dashboard
        </Link>

        {/* Page title row */}
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between mb-8 gap-3 sm:gap-4">
          <div>
            <h1 className="text-2xl font-bold font-display text-slate-900 tracking-tight">
              Your N-400 Application
            </h1>
            <div className="flex items-center gap-2 mt-2 flex-wrap">
              <StatusPill status={c.status} />
              {c.eligibility_status && <EligibilityPill status={c.eligibility_status} />}
              {isPaid && (
                <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-600 bg-emerald-50 px-2.5 py-1 rounded-full">
                  <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                  Fee paid
                </span>
              )}
            </div>
          </div>

          {/* Primary CTA */}
          <Link
            to={ctaHref}
            className="self-start sm:shrink-0 inline-flex items-center gap-2 rounded-xl bg-slate-900 text-white px-5 py-2.5 text-sm font-semibold hover:bg-slate-800 hover:-translate-y-0.5 hover:shadow-md transition-all duration-150"
          >
            {ctaLabel}
          </Link>
        </div>

        {/* Progress overview */}
        <ProgressOverview
          isPaid={isPaid}
          isEligible={isEligible}
          isReady={isReady}
          missingCount={missingCount}
          feeDollars={feeDollars}
          evalLoading={evalQ.isLoading}
          caseId={id!}
        />

        {/* Generated packet download */}
        {latestDoneJob && (
          <PacketDownloadCard caseId={id!} jobId={latestDoneJob.id} completedAt={latestDoneJob.completed_at} />
        )}

        {/* Readiness detail */}
        {!evalQ.isLoading && eval_ && (
          <div className="mt-5 bg-white rounded-2xl border border-slate-200/80 shadow-card p-6">
            <h2 className="font-semibold font-display text-slate-900 mb-4">Application checklist</h2>

            {isReady ? (
              <div className="flex items-center gap-2.5 text-sm text-emerald-700">
                <div className="w-5 h-5 rounded-full bg-emerald-500 flex items-center justify-center shrink-0">
                  <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                All required information is complete — your packet is ready to generate.
              </div>
            ) : (
              <div className="space-y-2">
                {eval_.missing_data.map((item) => (
                  <div key={item.key} className="flex items-start gap-2.5 text-sm text-slate-600">
                    <div className="w-5 h-5 rounded-full border-2 border-slate-200 flex items-center justify-center shrink-0 mt-0.5">
                      <div className="w-1.5 h-1.5 rounded-full bg-slate-300" />
                    </div>
                    {item.message}
                  </div>
                ))}
              </div>
            )}

            {eval_.warnings.length > 0 && (
              <details className="mt-4 text-sm">
                <summary className="cursor-pointer text-amber-600 font-medium hover:text-amber-700">
                  {eval_.warnings.length} warning{eval_.warnings.length !== 1 ? "s" : ""}
                </summary>
                <ul className="mt-2 space-y-1 text-slate-600 pl-2">
                  {eval_.warnings.map((w) => <li key={w.key}>· {w.message}</li>)}
                </ul>
              </details>
            )}
          </div>
        )}
      </main>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function ProgressOverview({
  isPaid, isEligible, isReady, missingCount, feeDollars, evalLoading, caseId
}: {
  isPaid: boolean; isEligible: boolean; isReady: boolean;
  missingCount: number; feeDollars: string; evalLoading: boolean; caseId: string;
}) {
  const steps = [
    {
      done: isEligible,
      label: "Eligibility confirmed",
      sub: isEligible ? "You qualify for naturalization" : "Complete eligibility check first",
      href: isEligible ? undefined : `/cases/${caseId}/gating`,
      cta: isEligible ? undefined : "Check eligibility →",
    },
    {
      done: !evalLoading && isReady,
      loading: evalLoading,
      label: "Application complete",
      sub: evalLoading
        ? "Checking…"
        : isReady
          ? "All sections filled in"
          : `${missingCount} section${missingCount !== 1 ? "s" : ""} remaining`,
      href: `/cases/${caseId}/wizard`,
      cta: isReady ? undefined : "Fill in details →",
    },
    {
      done: isPaid,
      label: "Filing fee paid",
      sub: isPaid ? `$${feeDollars} paid · ready to generate` : `$${feeDollars} USCIS fee required`,
      href: `/cases/${caseId}/wizard/checkout`,
      cta: isPaid ? undefined : "Pay now →",
    },
  ];

  return (
    <div className="bg-white rounded-2xl border border-slate-200/80 shadow-card p-6">
      <h2 className="font-semibold font-display text-slate-900 mb-5">Your progress</h2>
      <div className="space-y-4">
        {steps.map((s, i) => (
          <div key={i} className="flex items-center gap-4">
            {/* Indicator */}
            <div className={cn(
              "w-8 h-8 rounded-full flex items-center justify-center shrink-0 transition-all",
              s.done ? "bg-emerald-500" : s.loading ? "border-2 border-slate-200 border-t-blue-600 animate-spin" : "bg-slate-100",
            )}>
              {!s.loading && (
                s.done
                  ? <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5"><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
                  : <span className="text-xs font-bold text-slate-400">{i + 1}</span>
              )}
            </div>

            {/* Text */}
            <div className="flex-1 min-w-0">
              <div className={cn("text-sm font-semibold", s.done ? "text-slate-900" : "text-slate-500")}>
                {s.label}
              </div>
              <div className="text-xs text-slate-400 mt-0.5">{s.sub}</div>
            </div>

            {/* CTA */}
            {s.cta && s.href && (
              <Link
                to={s.href}
                className="shrink-0 text-xs font-semibold text-blue-600 hover:text-blue-700 px-3 py-1.5 rounded-lg hover:bg-blue-50 transition-colors"
              >
                {s.cta}
              </Link>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function StatusPill({ status }: { status: string }) {
  const map: Record<string, string> = {
    draft: "bg-slate-100 text-slate-600",
    in_progress: "bg-blue-50 text-blue-700",
    submitted: "bg-emerald-50 text-emerald-700",
    closed: "bg-slate-100 text-slate-400",
  };
  return (
    <span className={cn("text-xs font-semibold px-2.5 py-1 rounded-full", map[status] ?? "bg-slate-100 text-slate-600")}>
      {status.replace("_", " ")}
    </span>
  );
}

function PacketDownloadCard({ caseId, jobId, completedAt }: { caseId: string; jobId: string; completedAt?: string | null }) {
  const downloadMut = useMutation({
    mutationFn: () => cases.jobDownload(caseId, jobId),
    onSuccess: (data) => {
      window.open(data.download_url, "_blank", "noopener,noreferrer");
    },
  });

  const fmtDate = completedAt
    ? new Date(completedAt).toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit" })
    : null;

  return (
    <div className="mt-5 bg-white rounded-2xl border border-emerald-200 shadow-card p-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="font-semibold font-display text-slate-900 mb-1 flex items-center gap-2">
            <span className="w-5 h-5 rounded-full bg-emerald-500 flex items-center justify-center shrink-0">
              <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5">
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>
            </span>
            Your N-400 packet is ready
          </h2>
          {fmtDate && (
            <p className="text-xs text-slate-400">Generated {fmtDate}</p>
          )}
        </div>
        <button
          onClick={() => downloadMut.mutate()}
          disabled={downloadMut.isPending}
          className="shrink-0 inline-flex items-center gap-2 rounded-xl bg-emerald-600 text-white px-4 py-2.5 text-sm font-semibold hover:bg-emerald-700 disabled:opacity-60 transition-all"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
            <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
          </svg>
          {downloadMut.isPending ? "Preparing…" : "Download PDF"}
        </button>
      </div>
      {downloadMut.isError && (
        <p className="mt-3 text-xs text-red-600">Could not generate download link. Please try again.</p>
      )}
    </div>
  );
}

function EligibilityPill({ status }: { status: string }) {
  const map: Record<string, string> = {
    eligible: "bg-emerald-50 text-emerald-700",
    out_of_scope: "bg-slate-100 text-slate-500",
    manual_review: "bg-amber-50 text-amber-700",
  };
  const labels: Record<string, string> = {
    eligible: "✓ Eligible",
    out_of_scope: "Out of scope",
    manual_review: "⚠ Review needed",
  };
  return (
    <span className={cn("text-xs font-semibold px-2.5 py-1 rounded-full", map[status] ?? "bg-slate-100 text-slate-500")}>
      {labels[status] ?? status}
    </span>
  );
}
